/*
 * Copy for the whistleblowing form.
 *
 * Kept here rather than in the `translate` table like the rest of the site: this
 * is roughly 60 strings per language, most of them option values and validation
 * messages that are structural rather than editorial, and the admin Translations
 * page can only edit keys that already exist in that single JSON row. If the
 * compliance team needs to edit this copy themselves later, add it to
 * backend/seeds/translations.js and the next deploy will create the keys.
 */
export const whistleblowingText = {
    ENG: {
        header: "Whistleblowing",
        title: "Report a concern",
        intro: "This form is intended for reporting abuse, fraud, corruption or other violations relating to the activities of Armenian Card CJSC.",
        required: "Required",
        optionalNote: "Optional",

        categoryLabel: "State the subject of your report.",
        categoryPlaceholder: "Select a category",
        categories: {
            fraud: "Fraud or theft",
            corruption: "Corruption or bribery",
            conflict_of_interest: "Conflict of interest",
            money_laundering: "Money laundering",
            harassment: "Discrimination or harassment",
            data_protection: "Data protection breach",
            health_safety: "Health and safety",
            other: "Other"
        },
        categoryOtherLabel: "Please specify",

        subjectLabel: "Subject",
        subjectPlaceholder: "A short summary of the concern",

        descriptionLabel: "Description of the incident",
        descriptionPlaceholder: "Describe what happened, who was involved and how you became aware of it.",
        descriptionHint: "At most 5000 characters.",

        incidentDateLabel: "State the date on which the incident occurred.",
        ongoingLabel: "Is the incident ongoing?",
        locationLabel: "State the location of the incident",
        locationPlaceholder: "Department, office or location",
        personsInvolvedLabel: "Please name the people involved or connected to it (if known).",
        personsInvolvedPlaceholder: "Names or roles, if you know them",

        reportedBeforeLabel: "Have you previously reported this incident to anyone at Armenian Card CJSC?",
        reportedBeforeDetailsLabel: "State who you reported the incident to and what action was taken on your report.",

        witnessesLabel: "Are there people who can confirm the information provided?",
        witnessesDetailsLabel: "State the names and positions of those people.",

        relationshipLabel: "State your connection to Armenian Card CJSC",
        relationshipPlaceholder: "Select one",
        relationships: {
            employee: "Employee",
            former_employee: "Former employee",
            partner: "Partner or supplier",
            other: "Other"
        },

        anonymousLabel: "Do you want to report anonymously?",

        nameLabel: "Your name",
        emailLabel: "Your email",
        phoneLabel: "Your phone number",

        attachmentsLabel: "Supporting files",
        attachmentsHint: "PDF, JPEG or PNG only. Up to 5 files, 10 MB each, 20 MB total. No archives or Office files.",
        attachmentsChoose: "Choose files",
        attachmentsRemove: "Remove",

        consentLabel: "I confirm that the information I have provided is true to the best of my knowledge.",

        submit: "Submit report",
        submitting: "Sending...",

        yes: "Yes",
        no: "No",

        errorTitle: "The form could not be submitted",
        successTitle: "Thank you - your report has been received",
        successBody: "Your report has been sent to the compliance team. It will be reviewed confidentially.",
        successAgain: "Submit another report",

        validation: {
            category: "Please select a category.",
            categoryOther: "Please specify the category.",
            subject: "Please enter a subject.",
            description: "Please describe what happened.",
            descriptionTooLong: "The description is longer than 5000 characters.",
            relationship: "Please select your relationship to Arca.",
            reportedBefore: "Please answer this question.",
            reportedBeforeDetails: "Please describe the previous report.",
            witnesses: "Please answer this question.",
            witnessesDetails: "Please provide the witness details.",
            anonymous: "Please choose whether to report anonymously.",
            name: "Please enter your name.",
            email: "Please enter a valid email address.",
            consent: "Please confirm the declaration.",
            attachmentsCount: "You can attach at most 5 files.",
            attachmentsType: "Use PDF, JPEG or PNG files, each nonempty and no larger than 10 MB.",
            attachmentsSize: "The attachments are larger than 20 MB in total.",
            network: "The report could not be sent. Please check your connection and try again."
        }
    },

    ARM: {
        header: "Ահազանգում",
        title: "Ներկայացնել ահազանգ",
        intro: "Սույն ձևաթուղթը նախատեսված է «Արմենիան Քարդ» ՓԲԸ-ի գործունեության հետ կապված չարաշահման, խարդախության, կոռուպցիայի կամ այլ իրավախախտումների վերաբերյալ ահազանգ ներկայացնելու համար։",
        required: "Պարտադիր",
        optionalNote: "Ըստ ցանկության",

        categoryLabel: "Նշեք ահազանգի առարկան։",
        categoryPlaceholder: "Ընտրեք բաժինը",
        categories: {
            fraud: "Խարդախություն կամ գողություն",
            corruption: "Կոռուպցիա կամ կաշառակերություն",
            conflict_of_interest: "Շահերի բախում",
            money_laundering: "Փողերի լվացում",
            harassment: "Խտրականություն կամ ոտնձգություն",
            data_protection: "Տվյալների պաշտպանության խախտում",
            health_safety: "Աշխատանքի անվտանգություն և առողջություն",
            other: "Այլ"
        },
        categoryOtherLabel: "Խնդրում ենք նշել",

        subjectLabel: "Վերնագիր",
        subjectPlaceholder: "Ահազանգի հակիրճ նկարագրությունը",

        descriptionLabel: "Դեպքի նկարագրություն",
        descriptionPlaceholder: "Նկարագրեք, թե ինչ է տեղի ունեցել, ովքեր են ներգրավված եղել և ինչպես եք տեղեկացել այդ մասին։",
        descriptionHint: "Առավելագույնը 5000 նիշ։",

        incidentDateLabel: "Նշեք դեպքը տեղի ունենալու ամսաթիվը։",
        ongoingLabel: "Արդյո՞ք դեպքը շարունակական բնույթ է կրում։",
        locationLabel: "Նշեք դեպքի վայրը",
        locationPlaceholder: "Բաժին, գրասենյակ կամ վայր",
        personsInvolvedLabel: "Խնդրում ենք նշել ներգրավված կամ առնչվող անձանց (եթե հայտնի են)։",
        personsInvolvedPlaceholder: "Անուններ կամ պաշտոններ, եթե հայտնի են ձեզ",

        reportedBeforeLabel: "Նախկինում հայտնե՞լ եք այս դեպքի մասին Արմենիան Քարդ ՓԲԸ Ընկերությունում որևէ անձի:",
        reportedBeforeDetailsLabel: "Նշեք, թե ում եք հայտնել դեպքի մասին և ինչ ընթացք է տրվել Ձեր հաղորդմանը։",

        witnessesLabel: "Արդյո՞ք կան անձինք, որոնք կարող են հաստատել ներկայացված տեղեկությունները",
        witnessesDetailsLabel: "Նշեք տվյալ անձանց անունները և պաշտոնները։",

        relationshipLabel: "Նշեք Ձեր առնչությունը Արմենիան Քարդ ՓԲԸ Ընկերության հետ",
        relationshipPlaceholder: "Ընտրեք մեկը",
        relationships: {
            employee: "Աշխատակից",
            former_employee: "Նախկին աշխատակից",
            partner: "Գործընկեր կամ մատակարար",
            other: "Այլ"
        },

        anonymousLabel: "Ցանկանու՞մ եք ահազանգը ներկայացնել անանուն",

        nameLabel: "Ձեր անունը",
        emailLabel: "Ձեր էլ. հասցեն",
        phoneLabel: "Ձեր հեռախոսահամարը",

        attachmentsLabel: "Կցվող փաստաթղթեր",
        attachmentsHint: "Միայն PDF, JPEG կամ PNG։ Առավելագույնը 5 ֆայլ՝ յուրաքանչյուրը մինչև 10 ՄԲ, ընդհանուր՝ 20 ՄԲ։ Արխիվները և Office ֆայլերը չեն ընդունվում։",
        attachmentsChoose: "Ընտրել ֆայլեր",
        attachmentsRemove: "Հեռացնել",

        consentLabel: "Հաստատում եմ, որ տրամադրված տեղեկատվությունը, ինչքանով ինձ հայտնի է, ճշմարիտ է։",

        submit: "Ուղարկել ահազանգը",
        submitting: "Ուղարկվում է...",

        yes: "Այո",
        no: "Ոչ",

        errorTitle: "Ձևաթուղթը չհաջողվեց ուղարկել",
        successTitle: "Շնորհակալություն, ձեր ահազանգը ստացվել է",
        successBody: "Ձեր ահազանգն ուղարկվել է համապատասխանության թիմին։ Այն կքննարկվի գաղտնիության պահպանմամբ։",
        successAgain: "Ներկայացնել ևս մեկ ահազանգ",

        validation: {
            category: "Խնդրում ենք ընտրել բաժինը։",
            categoryOther: "Խնդրում ենք նշել բաժինը։",
            subject: "Խնդրում ենք լրացնել վերնագիրը։",
            description: "Խնդրում ենք նկարագրել կատարվածը։",
            descriptionTooLong: "Նկարագրությունը գերազանցում է 5000 նիշը։",
            relationship: "Խնդրում ենք ընտրել ձեր կապը Arca-ի հետ։",
            reportedBefore: "Խնդրում ենք պատասխանել այս հարցին։",
            reportedBeforeDetails: "Խնդրում ենք նկարագրել նախորդ դիմումը։",
            witnesses: "Խնդրում ենք պատասխանել այս հարցին։",
            witnessesDetails: "Խնդրում ենք նշել վկաների տվյալները։",
            anonymous: "Խնդրում ենք ընտրել՝ ահազանգը անանու՞ն է։",
            name: "Խնդրում ենք լրացնել ձեր անունը։",
            email: "Խնդրում ենք լրացնել վավեր էլ. հասցե։",
            consent: "Խնդրում ենք հաստատել հայտարարությունը։",
            attachmentsCount: "Կարող եք կցել առավելագույնը 5 ֆայլ։",
            attachmentsType: "Ընտրեք PDF, JPEG կամ PNG ֆայլեր՝ ոչ դատարկ և յուրաքանչյուրը մինչև 10 ՄԲ։",
            attachmentsSize: "Կցված ֆայլերի ընդհանուր ծավալը գերազանցում է 20 ՄԲ-ը։",
            network: "Ահազանգը չհաջողվեց ուղարկել։ Ստուգեք կապը և փորձեք կրկին։"
        }
    },

    RUS: {
        header: "Информирование о нарушениях",
        title: "Сообщить о нарушении",
        intro: "Настоящая форма предназначена для сообщения о злоупотреблениях, мошенничестве, коррупции или иных правонарушениях, связанных с деятельностью ЗАО «Армениан Кард».",
        required: "Обязательно",
        optionalNote: "Необязательно",

        categoryLabel: "Укажите предмет сообщения.",
        categoryPlaceholder: "Выберите категорию",
        categories: {
            fraud: "Мошенничество или хищение",
            corruption: "Коррупция или взяточничество",
            conflict_of_interest: "Конфликт интересов",
            money_laundering: "Отмывание денег",
            harassment: "Дискриминация или домогательства",
            data_protection: "Нарушение защиты данных",
            health_safety: "Охрана труда и безопасность",
            other: "Другое"
        },
        categoryOtherLabel: "Укажите, пожалуйста",

        subjectLabel: "Тема",
        subjectPlaceholder: "Краткое описание проблемы",

        descriptionLabel: "Описание происшествия",
        descriptionPlaceholder: "Опишите, что произошло, кто был вовлечён и как вам стало об этом известно.",
        descriptionHint: "Не более 5000 символов.",

        incidentDateLabel: "Укажите дату происшествия.",
        ongoingLabel: "Носит ли происшествие продолжающийся характер?",
        locationLabel: "Укажите место происшествия",
        locationPlaceholder: "Отдел, офис или место",
        personsInvolvedLabel: "Пожалуйста, укажите вовлечённых или причастных лиц (если они известны).",
        personsInvolvedPlaceholder: "Имена или должности, если они вам известны",

        reportedBeforeLabel: "Сообщали ли вы ранее об этом происшествии кому-либо в ЗАО «Армениан Кард»?",
        reportedBeforeDetailsLabel: "Укажите, кому вы сообщили о происшествии и какой ход был дан вашему сообщению.",

        witnessesLabel: "Есть ли лица, которые могут подтвердить представленную информацию?",
        witnessesDetailsLabel: "Укажите имена и должности этих лиц.",

        relationshipLabel: "Укажите вашу связь с ЗАО «Армениан Кард»",
        relationshipPlaceholder: "Выберите один вариант",
        relationships: {
            employee: "Сотрудник",
            former_employee: "Бывший сотрудник",
            partner: "Партнёр или поставщик",
            other: "Другое"
        },

        anonymousLabel: "Хотите отправить сообщение анонимно?",

        nameLabel: "Ваше имя",
        emailLabel: "Ваш адрес электронной почты",
        phoneLabel: "Ваш номер телефона",

        attachmentsLabel: "Подтверждающие файлы",
        attachmentsHint: "Только PDF, JPEG или PNG. До 5 файлов, до 10 МБ каждый, суммарно 20 МБ. Архивы и файлы Office не принимаются.",
        attachmentsChoose: "Выбрать файлы",
        attachmentsRemove: "Удалить",

        consentLabel: "Подтверждаю, что предоставленная информация, насколько мне известно, достоверна.",

        submit: "Отправить сообщение",
        submitting: "Отправка...",

        yes: "Да",
        no: "Нет",

        errorTitle: "Не удалось отправить форму",
        successTitle: "Спасибо, ваше сообщение получено",
        successBody: "Ваше сообщение направлено в отдел комплаенса. Оно будет рассмотрено конфиденциально.",
        successAgain: "Отправить ещё одно сообщение",

        validation: {
            category: "Пожалуйста, выберите категорию.",
            categoryOther: "Пожалуйста, укажите категорию.",
            subject: "Пожалуйста, укажите тему.",
            description: "Пожалуйста, опишите произошедшее.",
            descriptionTooLong: "Описание превышает 5000 символов.",
            relationship: "Пожалуйста, укажите ваше отношение к Arca.",
            reportedBefore: "Пожалуйста, ответьте на этот вопрос.",
            reportedBeforeDetails: "Пожалуйста, опишите предыдущее обращение.",
            witnesses: "Пожалуйста, ответьте на этот вопрос.",
            witnessesDetails: "Пожалуйста, укажите данные свидетелей.",
            anonymous: "Пожалуйста, выберите, отправлять ли сообщение анонимно.",
            name: "Пожалуйста, укажите ваше имя.",
            email: "Пожалуйста, укажите корректный адрес электронной почты.",
            consent: "Пожалуйста, подтвердите заявление.",
            attachmentsCount: "Можно прикрепить не более 5 файлов.",
            attachmentsType: "Используйте непустые файлы PDF, JPEG или PNG размером до 10 МБ каждый.",
            attachmentsSize: "Суммарный размер вложений превышает 20 МБ.",
            network: "Не удалось отправить сообщение. Проверьте соединение и попробуйте снова."
        }
    }
};

export default whistleblowingText;
